import type {Component, ComponentDefinition, Editor} from 'grapesjs';
import {
  CUSTOM_EVENT_COMPONENT_STYLE_COPIED,
  EMAIL_DATA_CLIPBOARD_KEY,
  EMAIL_MAKER_COMMANDS,
  EMAIL_MAKER_COMPONENT_TYPES,
  NONCOPYABLE_STYLES,
} from '@EmailMakerReact/email-maker-react.constants';
import {setComponentStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {sanitizeComponentContent} from '@EmailMakerReact/lib/email-maker-sanitize-library';
import {scrollToComponent, setDefaultSelection, SCROLL_TO_REVEALED_COMPONENT_DELAY_MS} from '@EmailMakerReact/lib/email-maker-selection-library';
import {removeComponentAnchor} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {trackGaEventForComponent} from '@EmailMakerReact/lib/email-maker-misc-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

// EMAIL_DATA_CLIPBOARD_KEY stores serialized component JSON shared cross-tab — keep its shape stable.
const CLIPBOARD_STATE_MODEL_KEY = 'pmwEmailMakerClipboardState';

interface EmailMakerClipboardState {
  copiedStylesComponent: Component;
}

/** Serializes the selection (minus bare cells) to the cross-tab localStorage clipboard. */
export function copySelectedComponents(editor: Editor): void {
  const comps = editor.getSelectedAll();

  if (comps.length === 0) {
    return;
  }

  const validComps = comps.filter((comp) => !comp.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL));

  if (validComps.length === 0) {
    return;
  }

  const serializedComps = validComps.map((comp) => comp.toJSON());
  window.localStorage.setItem(EMAIL_DATA_CLIPBOARD_KEY, JSON.stringify(serializedComps));
}

/** Pastes the localStorage clipboard next to the selection. */
export function pasteComponents(editor: Editor): void {
  const selected = editor.getSelected();

  if (!selected) {
    return;
  }

  const rawClipboard = window.localStorage.getItem(EMAIL_DATA_CLIPBOARD_KEY);
  if (!rawClipboard) {
    return;
  }

  // The shared clipboard key can hold corrupted data; a malformed payload aborts the paste.
  let components: ComponentDefinition[];
  try {
    components = JSON.parse(rawClipboard) as ComponentDefinition[];
  } catch (e: unknown) {
    logEmailMakerError('pasteComponents: clipboard JSON.parse failed', e);
    return;
  }

  const addedComponents = pasteComponentsNextTo(editor, selected, components);
  const firstPasted = addedComponents?.[0];
  if (firstPasted) {
    scrollToComponent(editor, {component: firstPasted, delay: SCROLL_TO_REVEALED_COMPONENT_DELAY_MS});
  }
}

/** Copies, then removes the selection. */
export function cutSelectedComponents(editor: Editor): void {
  copySelectedComponents(editor);
  removeSelectedComponents(editor);
}

/** A component view exposing GrapesJS' async rich-text teardown (`ComponentTextView`, not in the public types). */
interface RteEditableComponentView {
  disableEditing: () => Promise<void>;
}

function isRteEditableComponentView(view: unknown): view is RteEditableComponentView {
  return typeof view === 'object' && view !== null && 'disableEditing' in view && typeof view.disableEditing === 'function';
}

/**
 * Flushes any open rich-text editing session into its component model. GrapesJS only merges the
 * live contenteditable DOM into the model on RTE teardown, so a component duplicated mid-edit would
 * otherwise clone a stale model — dropping text typed since the session opened and keeping only what
 * had already reached the model (e.g. appended merge tags). Awaiting the teardown makes the clone
 * reflect the latest content.
 */
async function flushActiveRteSession(editor: Editor): Promise<void> {
  const editingComponent = editor.getEditing();
  if (!editingComponent) {
    return;
  }

  const editingView: unknown = editingComponent.getView();
  if (isRteEditableComponentView(editingView)) {
    await editingView.disableEditing();
  }
}

/**
 * Inserts a clone of the selected component immediately after it in its parent, then selects the clone.
 * Deliberately bypasses copySelectedComponents/pasteComponents so the keyboard copy/paste path is unaffected.
 */
export async function duplicateSelectedComponent(editor: Editor): Promise<void> {
  const selected = editor.getSelected();
  if (!selected || selected.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL)) {
    return;
  }

  const parent = selected.parent();
  if (!parent) {
    return;
  }

  await flushActiveRteSession(editor);

  const clone = selected.clone();
  sanitizeComponentContent(clone);
  parent.components().add(clone, {at: selected.index() + 1});
  editor.select(clone);
  trackGaEventForComponent(editor, EMAIL_MAKER_COMMANDS.DUPLICATE_EVENT_ACTION, clone);
}

/** Removes every selected component (anchor unwrap, core delete, default reselect). */
export function removeSelectedComponents(editor: Editor): void {
  editor.getSelectedAll().forEach((component) => {
    removeComponentAnchor(editor, component);
    editor.runCommand('core:component-delete', {component});
    setDefaultSelection(editor);
  });
}

/** Toggles the selection's draggability. */
export function toggleComponentLock(editor: Editor): void {
  const component = editor.getSelected();
  if (!component) {
    return;
  }

  component.set('draggable', !component.get('draggable'));
}

/** Remembers the component whose styles can be pasted, and notifies listeners. */
export function copyComponentStyles(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  const state: EmailMakerClipboardState = {copiedStylesComponent: target};
  editor.getModel().set(CLIPBOARD_STATE_MODEL_KEY, state);
  document.dispatchEvent(new CustomEvent(CUSTOM_EVENT_COMPONENT_STYLE_COPIED, {}));
}

/** Whether a component's styles have been copied this session (gates the Paste-styles button). */
export function hasCopiedComponentStyles(editor: Editor): boolean {
  return editor.getModel().get(CLIPBOARD_STATE_MODEL_KEY) !== undefined;
}

/** Applies the remembered component's styles (minus non-copyable ones) to the component. */
export function pasteComponentStyles(editor: Editor, component?: Component): void {
  const state = editor.getModel().get(CLIPBOARD_STATE_MODEL_KEY) as EmailMakerClipboardState | undefined;
  if (!state) {
    return;
  }

  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  const styles = state.copiedStylesComponent.getStyle();

  for (const [property, value] of Object.entries(styles)) {
    if (NONCOPYABLE_STYLES.includes(property)) {
      continue;
    }

    // Stacked (array) style values never occur on email components; only strings are forwarded.
    if (typeof value === 'string') {
      setComponentStyle(editor, {component: target, property, value});
    }
  }
}

/**
 * Inserts clipboard components as a sibling immediately after the anchor, into anchor.parent() at anchor.index()+1.
 * When the anchor's parent does not accept the content, walks up one level and re-derives the insertion index
 * from the new anchor — never carrying a stale index across levels.
 * Terminates at the content-container level (the droppable root for email content).
 */
function pasteComponentsNextTo(editor: Editor, anchor: Component, components: ComponentDefinition[]): Component[] | undefined {
  const parent = anchor.parent();
  if (!parent) {
    return undefined;
  }

  const insertionIndex = anchor.index() + 1;
  // GrapesJS accepts an array source at runtime; its canMove typing only declares the single-item forms.
  const isValid = editor.Components.canMove(parent, components as unknown as ComponentDefinition, insertionIndex).result;

  if (isValid) {
    const addedComponents = parent.components().add(components, {at: insertionIndex});
    editor.trigger('component:paste', addedComponents);
    parent.emitUpdate();
    addedComponents.forEach((addedComponent) => {
      sanitizeComponentContent(addedComponent);
    });
    return addedComponents;
  }

  if (parent.get('type') === EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER) {
    return undefined;
  }

  return pasteComponentsNextTo(editor, parent, components);
}
